import React, { useEffect, useMemo, useRef, useState } from "react";
import { Picker } from "emoji-mart";

import AuthField from "../Auth/AuthField";
import ModalImageChatbot from "../ModalImageChatbot";
import TemplateConversationPreview from "../TemplateConversationPreview";
import {
	FormField,
	SegmentControl,
	IconAttach,
	IconSmile,
	IconClear,
} from "./bulkMessagingUi";
import { TemplatesTable } from "./TemplatesTable";

const ALLOWED_CHANNELS = ["email", "whatsapp", "telegram", "whatsappcloud", "whatsapp_business_account"];

const CHANNEL_LABELS = {
	email: "E-mail",
	whatsapp: "WhatsApp",
	telegram: "Telegram",
	whatsappcloud: "WhatsApp Cloud",
	whatsapp_business_account: "WhatsApp Cloud",
};

function getMediaAccept(format) {
	if (format === "IMAGE") return "image/png, image/jpeg, image/jpg";
	if (format === "VIDEO") return "video/mp4";
	if (format === "DOCUMENT") return "application/pdf";
	return "";
}

export default function BulkMessagingCampaignStep({
	bulkMessage,
	setBulkMessage,
	channel,
	setChannel,
	whatsApps,
	templateParams,
	setTemplateParams,
	templateParamsLength,
	handleMessage,
	handleAddEmoji,
	showEmoji,
	setShowEmoji,
	darkMode,
	handleUpdateMedia,
	setContacts,
	setSelectedContacts,
	setHasMoreContacts,
	setContactsPage,
	setToTransferContacts,
	loadingContacts,
	tags = [],
}) {
	const emojiRef = useRef(null);

	const filteredWhatsApps = useMemo(
		() => whatsApps.filter((whatsApp) => ALLOWED_CHANNELS.includes(whatsApp.channel)),
		[whatsApps]
	);


	const connectionId = bulkMessage?.bulkMessagingData?.connectionId ?? "";
	const templateFormat = String(
		bulkMessage?.bulkMessagingData?.template?.components?.find(
			(c) => String(c?.type || "").toUpperCase() === "HEADER"
		)?.format || ""
	).toUpperCase();
	const isWhatsAppCloud =
		channel === "whatsappcloud" ||
		channel === "whatsapp_business_account" ||
		(!channel &&
			["whatsappcloud", "whatsapp_business_account"].includes(
				bulkMessage?.bulkMessagingData?.connection?.channel
			));
	const messageLength = bulkMessage?.bulkMessagingData?.message?.length || 0;
	const selectedTagIds = Array.isArray(bulkMessage?.bulkMessagingData?.tagIds)
		? bulkMessage.bulkMessagingData.tagIds.map((id) => Number(id))
		: [];

	const toggleTag = (tagId) => {
		const id = Number(tagId);
		const next = selectedTagIds.includes(id)
			? selectedTagIds.filter((current) => current !== id)
			: [...selectedTagIds, id];
		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				tagIds: next,
			},
		});
	};
	const mediaPath = bulkMessage?.bulkMessagingData?.mediaPath;
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);

	useEffect(() => {
		if (!connectionId) return;
		const next = filteredWhatsApps.find(
			(whatsApp) => String(whatsApp.id) === String(connectionId)
		);
		if (next) {
			setSelectedWhatsApp(next);
		}
	}, [connectionId, filteredWhatsApps]);

	useEffect(() => {
		if (!showEmoji) return undefined;

		const handleClickOutside = (event) => {
			if (emojiRef.current && !emojiRef.current.contains(event.target)) {
				setShowEmoji(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("touchstart", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchstart", handleClickOutside);
		};
	}, [showEmoji, setShowEmoji]);

	const handleConnectionChange = (event) => {
		const next = filteredWhatsApps.find(
			(whatsApp) => String(whatsApp.id) === event.target.value
		);
		if (!next) return;

		const channelChanged = channel !== next.channel;
		setSelectedWhatsApp(next);
		setChannel(next.channel);

		if (channelChanged) {
			setContacts([]);
			setSelectedContacts([]);
			setHasMoreContacts(false);
			setContactsPage(1);
			setToTransferContacts([]);
		}

		setBulkMessage({
			...bulkMessage,
			...(channelChanged ? { contacts: [] } : {}),
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				connectionId: next.id,
				connection: next,
			},
		});
	};

	const handleClearMedia = () => {
		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				mediaPath: null,
			},
		});
	};

	const syncTemplateParams = (nextParams) => {
		setTemplateParams(nextParams);
		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				templateParams: nextParams,
			},
		});
	};

	const handleTemplateParamTypeChange = (index, type) => {
		const newTemplateParams = [...templateParams];
		newTemplateParams[index] = type === "contactName" ? "contactName" : "";
		syncTemplateParams(newTemplateParams);
	};

	const handleTemplateParamValueChange = (index, value) => {
		const newTemplateParams = [...templateParams];
		newTemplateParams[index] = value;
		syncTemplateParams(newTemplateParams);
	};

	return (
		<div className="bulk-messaging-campaign-step flex w-full flex-col gap-5">
			<div className="bulk-messaging-wizard__form-row">
				<FormField id="bulk-campaign-name" label="Nome da campanha">
					<input
						id="bulk-campaign-name"
						type="text"
						className="form-input w-full"
						value={bulkMessage?.bulkMessagingData?.name || ""}
						onChange={(event) => {
							setBulkMessage({
								...bulkMessage,
								bulkMessagingData: {
									...bulkMessage.bulkMessagingData,
									name: event.target.value,
								},
							});
						}}
						required
					/>
				</FormField>

				<FormField id="bulk-campaign-connection" label="Conexão de origem">
					<select
						id="bulk-campaign-connection"
						className="form-select w-full"
						value={connectionId ? String(connectionId) : ""}
						onChange={handleConnectionChange}
						required
					>
						<option value="" disabled>
							Selecione uma conexão
						</option>
						{filteredWhatsApps.map((whatsApp) => (
							<option key={whatsApp.id} value={String(whatsApp.id)}>
								{`${CHANNEL_LABELS[whatsApp.channel] || whatsApp.channel} — ${whatsApp.name}`}
							</option>
						))}
					</select>
				</FormField>
			</div>

			<FormField
				id="bulk-campaign-tags"
				label="Tags dos contatos"
				hint="Opcional. Essas tags são aplicadas nos contatos desta campanha, sem remover as que eles já têm."
			>
				{tags.length === 0 ? (
					<p className="m-0 text-sm text-default-500 dark:text-default-400">
						Nenhuma tag cadastrada.
					</p>
				) : (
					<div className="flex flex-wrap gap-2">
						{tags.map((tag) => {
							const selected = selectedTagIds.includes(Number(tag.id));
							return (
								<button
									key={tag.id}
									type="button"
									className={`rounded-full border px-3 py-1 text-sm ${
										selected
											? "border-primary bg-primary/15 font-medium text-primary"
											: "border-default-300 text-default-700 hover:border-primary/40 dark:border-default-600 dark:text-default-200"
									}`}
									onClick={() => toggleTag(tag.id)}
								>
									{tag.name}
								</button>
							);
						})}
					</div>
				)}
			</FormField>

			{isWhatsAppCloud ? (
				<div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] lg:items-start">
					<div className="flex min-w-0 flex-col gap-4">
						<div className="max-h-[35vh] w-full overflow-auto rounded-lg border border-default-200 dark:border-default-700">
							<TemplatesTable
								whatsappId={connectionId || selectedWhatsApp?.id}
								setBulkMessage={setBulkMessage}
								bulkMessage={bulkMessage}
							/>
						</div>

						{templateParamsLength > 0 && (
							<p className="m-0 text-sm font-medium text-default-700 dark:text-default-300">
								Parâmetros do template: {templateParamsLength}
							</p>
						)}

						{["VIDEO", "DOCUMENT", "IMAGE"].includes(templateFormat) && (
							<AuthField
								id="bulk-template-media"
								label="Mídia do template"
								hint="Cole uma URL pública (https://...) ou anexe um arquivo"
							>
								<div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
									<div className="relative min-w-0 flex-1">
										{mediaPath && templateFormat === "IMAGE" && (
											<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
												<div className="size-10 overflow-hidden rounded-lg">
													<ModalImageChatbot imageUrl={mediaPath} />
												</div>
											</div>
										)}
										<input
											id="bulk-template-media"
											type="url"
											className={`form-input w-full ${mediaPath && templateFormat === "IMAGE" ? "ps-14" : ""}`}
											value={mediaPath || ""}
											placeholder="https://exemplo.com/imagem.jpg"
											onChange={(event) => {
												const next = event.target.value;
												setBulkMessage({
													...bulkMessage,
													bulkMessagingData: {
														...bulkMessage.bulkMessagingData,
														mediaPath: next.trim() ? next : null,
													},
												});
											}}
										/>
									</div>
									<div className="flex shrink-0 items-center gap-2">
										{mediaPath && (
											<button
												type="button"
												className="btn btn-sub-gray inline-flex items-center gap-1.5"
												onClick={handleClearMedia}
												title="Remover mídia"
											>
												<IconClear className="size-4" />
												Remover
											</button>
										)}
										<label
											className="btn btn-primary inline-flex cursor-pointer items-center gap-1.5"
											title="Anexar mídia"
										>
											<IconAttach />
											Anexar
											<input
												type="file"
												className="sr-only"
												accept={getMediaAccept(templateFormat)}
												onChange={handleUpdateMedia}
											/>
										</label>
									</div>
								</div>
							</AuthField>
						)}

						{templateParamsLength > 0 && (
							<div className="w-full rounded-lg border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900/40">
								<div className="space-y-4">
									{[...Array(templateParamsLength)].map((_, index) => {
										const paramType =
											templateParams[index] === "contactName" ? "contactName" : "text";

										return (
											<div
												key={index}
												className="flex flex-col gap-3 sm:flex-row sm:items-center"
											>
												<span className="shrink-0 text-sm font-medium text-default-700 dark:text-default-300">{`{{${index + 1}}}`}</span>
												<SegmentControl
													options={[
														{ value: "text", label: "Texto Livre" },
														{ value: "contactName", label: "Nome do contato" },
													]}
													value={paramType}
													onChange={(value) => handleTemplateParamTypeChange(index, value)}
												/>
												{paramType !== "contactName" && (
													<input
														type="text"
														className="form-input min-w-0 flex-1"
														placeholder={`Parâmetro ${index + 1}`}
														value={templateParams[index] || ""}
														onChange={(event) =>
															handleTemplateParamValueChange(index, event.target.value)
														}
													/>
												)}
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>

					<aside className="lg:sticky lg:top-0">
						<TemplateConversationPreview
							template={bulkMessage?.bulkMessagingData?.template || null}
							params={templateParams}
							mediaPath={mediaPath}
							darkMode={darkMode}
						/>
						<p className="mb-0 mt-2 text-center text-[11px] text-default-500">
							Preview atualiza conforme você escolhe o template e preenche os parâmetros
						</p>
					</aside>
				</div>
			) : (
				<>
					<AuthField
						id="bulk-campaign-message"
						label="Mensagem"
						labelAction={
							<span className="float-end text-sm text-default-500 dark:text-default-400">
								{`${messageLength}/1024`}
							</span>
						}
					>
						<div className="relative">
							<textarea
								id="bulk-campaign-message"
								className="form-textarea w-full min-h-[8rem] resize-y pe-10"
								value={bulkMessage?.bulkMessagingData?.message || ""}
								onChange={handleMessage}
								maxLength={1024}
								rows={4}
								required
							/>
							<button
								type="button"
								className="absolute end-2 top-2 inline-flex size-8 items-center justify-center rounded-md text-default-500 transition-colors hover:bg-default-100 hover:text-default-700 dark:hover:bg-default-800 dark:hover:text-default-200"
								aria-label="Abrir seletor de emoji"
								onClick={() => setShowEmoji((prevState) => !prevState)}
							>
								<IconSmile />
							</button>
							{showEmoji && (
								<div
									ref={emojiRef}
									className="absolute bottom-full z-10 mb-2 w-full max-w-sm border border-default-200 bg-card shadow-lg dark:border-default-700"
								>
									<Picker
										perLine={16}
										showPreview={false}
										showSkinTones={false}
										theme={darkMode ? "dark" : "light"}
										onSelect={handleAddEmoji}
									/>
								</div>
							)}
						</div>
					</AuthField>

					<p className="-mt-2 mb-0 text-sm italic text-default-500 dark:text-default-400">
						Observação: para inserir variáveis na mensagem digite "$" e defina um
						whatsapp não oficial para obter o nome original do contato.
					</p>

					<AuthField id="bulk-campaign-file" label="Arquivo">
						<div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
							<div className="relative min-w-0 flex-1">
								{mediaPath && (
									<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
										<div className="size-12 overflow-hidden rounded-lg">
											<ModalImageChatbot imageUrl={mediaPath} />
										</div>
									</div>
								)}
								<input
									id="bulk-campaign-file"
									type="text"
									className={`form-input w-full ${mediaPath ? "ps-16" : ""}`}
									value={mediaPath || ""}
									readOnly
									disabled
									placeholder="Nenhum arquivo selecionado"
								/>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								{mediaPath && (
									<button
										type="button"
										className="btn btn-sub-gray"
										onClick={handleClearMedia}
									>
										remover
									</button>
								)}
								<label
									className={`btn btn-primary shrink-0 cursor-pointer sm:self-stretch ${loadingContacts ? "pointer-events-none opacity-60" : ""
										}`}
								>
									Procurar
									<input
										type="file"
										className="sr-only"
										disabled={loadingContacts}
										onChange={handleUpdateMedia}
									/>
								</label>
							</div>
						</div>
					</AuthField>
				</>
			)}
		</div>
	);
}
